import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import { SPAM_GUIDE_INTRO, SPAM_GUIDE_SECTIONS } from './spamGuideContent';

// 로그인 인증 문자 스팸함 확인 방법 — 아래에서 올라오는 창 (대표 7685, 2026-10-21)
// 기기별·통신사별 단계는 spamGuideContent.ts 한 곳에만 둔다. 직접 확인했거나 공식 안내에 있는 단계만 쓴다.
interface SpamGuideSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const SpamGuideSheet: React.FC<SpamGuideSheetProps> = ({ open, onOpenChange }) => {
    const [tabId, setTabId] = useState<string>(SPAM_GUIDE_SECTIONS[0].id);
    const section = SPAM_GUIDE_SECTIONS.find((s) => s.id === tabId) ?? SPAM_GUIDE_SECTIONS[0];

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                className="mx-auto max-h-[88dvh] max-w-md overflow-y-auto rounded-t-[28px] border-[#262626] bg-[#121212] p-0 sm:bottom-6 sm:rounded-[28px] sm:border"
            >
                <div className="px-5 pb-7 pt-7" style={{ wordBreak: 'keep-all' }}>
                    <SheetTitle className="text-[18px] font-bold text-[#FAFAFA]">스팸함 확인 방법</SheetTitle>
                    <SheetDescription className="mt-1.5 text-[13px] leading-relaxed text-[#A8A8A8]">
                        {SPAM_GUIDE_INTRO}
                    </SheetDescription>

                    <div role="tablist" aria-label="기기와 통신사" className="mt-4 flex gap-1.5 overflow-x-auto pb-1">
                        {SPAM_GUIDE_SECTIONS.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                role="tab"
                                aria-selected={s.id === section.id}
                                onClick={() => setTabId(s.id)}
                                className={`flex-shrink-0 rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors ${s.id === section.id
                                    ? 'border-[#3182F6] bg-[#3182F6]/15 text-[#FFFFFF]'
                                    : 'border-[#262626] bg-[#181818] text-[#A8A8A8] hover:border-[#363636]'
                                    }`}
                            >
                                {s.tab}
                            </button>
                        ))}
                    </div>

                    <div role="tabpanel" className="mt-4 space-y-5">
                        {section.groups.map((g) => (
                            <div key={g.title ?? section.id}>
                                {g.title && <p className="mb-2 text-[13px] font-semibold text-[#FFFFFF]">{g.title}</p>}
                                <ol className="space-y-2">
                                    {g.steps.map((step, i) => (
                                        <li key={step} className="flex gap-2.5 text-[13px] leading-relaxed text-[#E0E0E0]">
                                            <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#262626] text-[11px] font-semibold text-[#FFFFFF]">
                                                {i + 1}
                                            </span>
                                            <span>{step}</span>
                                        </li>
                                    ))}
                                </ol>
                                {g.note && <p className="mt-2 text-[12px] leading-relaxed text-[#8E8E8E]">{g.note}</p>}
                                {g.sourceUrl && (
                                    <a
                                        href={g.sourceUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#777777] underline underline-offset-2 hover:text-[#A8A8A8]"
                                    >
                                        {g.sourceLabel}
                                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>

                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="mt-6 h-11 w-full rounded-xl bg-[#262626] text-[14px] font-semibold text-[#FFFFFF] transition-colors hover:bg-[#363636]"
                    >
                        닫기
                    </button>
                </div>
            </SheetContent>
        </Sheet>
    );
};
